import { CheckOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import { useEffect, useState } from 'react';
import {
  ServiceAreaSize,
  useV2Gateway_CreateServiceAreasMutation,
} from 'Generated/graphql';
import { DefaultServiceAreaName } from 'Common/functions/ServiceArea';
import { Flex, Spinner, Box } from 'theme-ui';
import Edit from 'Images/svg/edit.turbopack-temp-svg';
import Theme from 'Common/themePeazy.json';
import { UseLocation } from 'Common/hooks/useLocation';
import useServiceAreasForLocation from 'Common/hooks/useServiceAreasForLocation';

type ServiceAreaSelectProps = {
  onSelect?: (serviceAreaUid: string) => void;
  location: Pick<UseLocation, 'uid' | 'type'>;
  onLoadingChange?: (l: boolean) => void;
  value?: string | null;
  color?: string;
  inline?: boolean;
  label?: string;
  disabled: boolean;
};

const ServiceAreaSelect: React.FC<ServiceAreaSelectProps> = ({
  onSelect,
  location,
  onLoadingChange,
  value = '',
  color = Theme.colors.black,
  label,
  disabled,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [serviceAreasForLocation, refetchServiceAreasForLocation] =
    useServiceAreasForLocation(location.uid);
  const [newServiceAreaName, setNewServiceAreaName] = useState<
    string | undefined
  >();
  const [createServiceAreas, createServiceAreasQuery] =
    useV2Gateway_CreateServiceAreasMutation();
  const [searchOpen, setSearchOpen] = useState(false);
  const valueIsValid = serviceAreasForLocation?.some(sa => sa.uid === value);

  const newServiceAreaOption = newServiceAreaName ? (
    <Select.Option value={newServiceAreaName}>
      {newServiceAreaName}
    </Select.Option>
  ) : null;

  useEffect(() => {
    onLoadingChange && onLoadingChange(createServiceAreasQuery.loading);
    refetchServiceAreasForLocation();
  }, [
    createServiceAreasQuery.data,
    createServiceAreasQuery.loading,
    location.uid,
  ]);

  useEffect(() => {
    if (serviceAreasForLocation) {
      const valueIsValid = serviceAreasForLocation?.some(sa => sa.uid === value);
      if (!valueIsValid) {
        onSelect && onSelect('');
      }
    }
  }, [serviceAreasForLocation]);

  const createNewServiceArea = () => {
    const isDuplicateServiceAreaName = serviceAreasForLocation?.some(
      sa => sa.name === newServiceAreaName,
    );

    if (newServiceAreaName && !isDuplicateServiceAreaName) {
      setIsLoading(true);
      createServiceAreas({
        variables: {
          data: [{
            externalUid: location.uid,
            name: newServiceAreaName,
            size: ServiceAreaSize.Medium,
            description: '',
          }]
        }
      })
        .then(res => {
          onSelect &&
            res.data?.createServiceAreas?.length &&
            res.data?.createServiceAreas[0].uid &&
            onSelect(res.data?.createServiceAreas[0].uid);

          setIsLoading(false);
          setNewServiceAreaName('');
        })
        .catch(unused => {
          setIsLoading(false);
        });
    }
  };

  return (
    <Box sx={{
      p: 0,
      width: '100%',
      mt: ['1.5rem', '2rem', '2rem']
    }}>
      {label && (
        <h3 className="text-2xl font-bold">
          {label}
        </h3>
      )}
      {isLoading ? (
        <Box sx={{
          mt: ['.8rem', '1.8rem', '1.8rem'],
          mb: ['0', '0', '20px'],
        }}>
          <Spinner size={32} />
        </Box>
      ) : (
        <Flex
          sx={{
            border: '1px solid #D9D9D9',
            alignItems: 'center',
            position: 'relative',
            mt: ['.8rem', '1.8rem', '1.8rem'],
            mb: ['0', '0', '20px'],
            height: '40px',
            borderRadius: '4px',
            '& .ant-select': {
              width: '100%',
            },
            '& .ant-select-selection': {
              color,
              border: 'none',
              boxShadow: 'none',
              cursor: 'pointer',
            },
            '& svg': {
              position: 'absolute'
            },
            '& .ant-select-selection-item': {
              pl: '1rem !important',
            }
          }}
        >
          {serviceAreasForLocation ? (
            <Select
              value={valueIsValid ? value : ''}
              optionFilterProp="children"
              onSelect={(s: string) => {
                onSelect && onSelect(s);
              }}
              onSearch={v => {
                setNewServiceAreaName(v);
              }}
              showSearch
              defaultValue=""
              onDropdownVisibleChange={o => {
                setSearchOpen(o);
                if (!o && newServiceAreaName) {
                  createNewServiceArea();
                }
              }}
              suffixIcon={null}
              disabled={disabled}
            >
              <Select.Option value="">
                {DefaultServiceAreaName[location.type]}
              </Select.Option>
              {serviceAreasForLocation?.map(sa => (
                <Select.Option key={sa.uid} value={sa.uid}>
                  {sa.name}
                </Select.Option>
              ))}
              {newServiceAreaOption}
            </Select>
          ) : null}
          <Edit sx={{
            position: 'relative',
            right: '1rem'
          }}
          />
          {searchOpen && (
            <CheckOutlined
              onClick={() => createNewServiceArea()}
              style={{
                cursor: 'pointer',
                position: 'absolute',
                right: '-2rem',
                padding: '0.5rem',
                top: 0,
              }}
            />
          )}
        </Flex>
      )}
    </Box>
  );
};

export default ServiceAreaSelect;
